import { useEffect, useRef } from 'react';

import clsx from 'clsx';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

import PropTypes from 'prop-types';

dayjs.extend(utc);
dayjs.extend(timezone);

import Icon from '@/components/base/Icon';

const collapse = (element) => {
    // get the height of the element
    const sectionHeight = element.offsetHeight;

    // temporarily disable all css transitions
    let elementTransition = element.style.transition;
    element.style.transition = '';

    // as soon as the previous style change has taken effect,
    // explicitly set the element's height to its current pixel height, so we
    // aren't transitioning out of 'auto'
    requestAnimationFrame(function () {
        element.style.height = sectionHeight + 'px';
        element.style.transition = elementTransition;

        // as soon as the previous style change has taken effect,
        // have the element transition to height: 0
        requestAnimationFrame(function () {
            element.style.height = 0 + 'px';
        });
    });

    // mark the section as "currently collapsed"
    element.setAttribute('data-collapsed', 'true');
};

const expand = (element, parent) => {
    // get the height of the parent minus span and button
    const sectionHeight = parent.offsetHeight - 48;
    // have the element transition to the desired height
    element.style.height = sectionHeight + 'px';

    // when the css transition finishes
    element.addEventListener('transitionend', function () {
        // remove this event listener so it only gets triggered once
        element.removeEventListener('transitionend', arguments.caller);
    });

    // mark the section as "currently not collapsed"
    element.setAttribute('data-collapsed', 'false');
};

const UtcDisplay = ({ timezone, setShowMore, showMore }) => {
    const utcFormat = dayjs().utc().tz(timezone).format('Z');
    const expandableRef = useRef(null);
    const parentRef = useRef(null);

    useEffect(() => {
        if (!expandableRef?.current || !parentRef?.current) {
            return;
        }

        //If shouldShowMore expand section
        if (
            showMore?.state &&
            showMore?.isShowingMore &&
            expandableRef?.current?.offsetHeight === 0
        ) {
            expand(expandableRef?.current, parentRef?.current);
        }
        //If !shouldShowMore collapse section
        else if (!showMore?.isShowingMore) {
            collapse(expandableRef?.current);
        }
    }, [showMore?.isShowingMore, showMore?.state]);

    return (
        <div
            ref={parentRef}
            className={clsx('flex h-full w-full flex-1 flex-col items-end pr-2.5', {
                'justify-start pt-1': showMore?.shouldShowMore,
                'justify-center py-1.5': !showMore?.shouldShowMore
            })}>
            <span className="inline-flex items-center text-9 text-icoGray-500">
                UTC {utcFormat}
            </span>
            <span ref={expandableRef} className={clsx('transform-all block duration-300')} />
            {showMore?.state ? (
                <button
                    className="mt-3.5 flex h-5 w-5 items-center justify-center rounded-full hover:bg-icoGray-100 hover:dark:bg-white/10"
                    onClick={(e) => {
                        e.stopPropagation();
                        setShowMore((prevState) => ({
                            ...prevState,
                            state: false,
                            isShowingMore: false
                        }));
                    }}>
                    <Icon
                        icon="chevron-top"
                        className="text-base text-icoGray-500 dark:text-white"
                    />
                </button>
            ) : null}
            {!showMore?.state && showMore?.shouldShowMore ? (
                <button
                    className="mt-3.5 flex h-5 w-5 items-center justify-center rounded-full hover:bg-icoGray-100 hover:dark:bg-white/10"
                    onClick={(e) => {
                        e.stopPropagation();
                        setShowMore((prevState) => ({
                            ...prevState,
                            state: true,
                            isShowingMore: true
                        }));
                    }}>
                    <Icon
                        icon="chevron-bottom"
                        className="text-base text-icoGray-500 dark:text-white"
                    />
                </button>
            ) : null}
        </div>
    );
};

UtcDisplay.propTypes = {
    timezone: PropTypes.string.isRequired,
    setShowMore: PropTypes.func,
    showMore: PropTypes.object
};

export default UtcDisplay;
